<template>
  <el-dialog
    :fullscreen="isMobile"
    :visible.sync="isVisible"
    custom-class="approval-remark-popup"
    width="38%"
    center
    destroy-on-close
    append-to-body
  >
    <div slot="title" class="text-left">
      <b>{{
        type == "APPROVED" ? $t("Approval Remarks") : $t("Rejection Remarks")
      }}</b>
    </div>
    <div :class="{ mobile: isMobile }">
      <p>{{ $t('REMARK_REQUIRED') }}</p>
      <el-form 
        ref="remarkForm"
        :model="formData"
        :rules="rules"
      >
        <el-form-item
          :label="$t('REMARK')"
          prop="remark"
          class="is-required"
        >
          <el-input
            v-model="formData.remark"
            type="textarea"
            resize="none"
            :rows="3"
            maxlength="60"
            show-word-limit
          >
          </el-input>
        </el-form-item>
      </el-form>
    </div>
    <div slot="footer" class="dialog-footer">
      <el-button @click="closeDialog" class="w-25 btn-cancel">{{
        $t("CANCEL")
      }}</el-button>
      <el-button class="button primary" @click="handleProceed">{{ $t("PROCEED") }}</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { generalMixin } from "@/utils/general-mixin.js";
import { approveArticle } from "@/api/article.js";

/**
 * @component ApprovalRemarkDialog
 * @description Dialog component for submitting approval or rejection remarks for an article
 */
export default {
  name: 'ApprovalRemarkDialog',
  mixins: [generalMixin],
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    type: { 
      type: String, 
      required: true,
      validator: (value) => ['APPROVED', 'REJECTED'].includes(value)
    },
    articleId: { 
      type: Number, 
      required: true 
    },
  },
  data() {
    return {
      formData: {
        remark: ''
      },
      rules: {
        remark: [
          { required: true, message: 'Please enter a remark', trigger: 'blur' },
          { min: 1, max: 60, message: 'Length should be 1 to 60 characters', trigger: 'blur' }
        ]
      }
    };
  },
  computed: {
    isVisible: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
  },
  methods: {
    closeDialog() {
      this.isVisible = false;
    },    
    showSuccessNotification() {
      const message = this.type === "APPROVED" 
        ? this.$t("ARTICLE_APPROVED_SUCCESSFULLY") 
        : this.$t("ARTICLE_REJECTED_SUCCESSFULLY");
        
      this.$notify({
        type: "success",
        message,
        duration: this.NOTIFY_DURATION,
        position: this.NOTIFY_POSITION
      });
    },
    submitApprovalRequest() {
      return approveArticle({
        article: {
          id: this.articleId
        },
        actionStatus: this.type,
        remark: this.formData.remark
      });
    },
    
    /**
     * Handles the form submission and article approval/rejection process
     * Validates the form, submits the API request, and shows appropriate notifications
     */
    handleProceed() {
      this.$refs.remarkForm.validate((valid) => {
        if (!valid) {
          return false;
        }
        
        this.submitApprovalRequest()
          .then((res) => {
            if (res.status === "ok") {
              this.showSuccessNotification();
              
              // Close dialog and navigate back to article listing page
              this.closeDialog();
              this.$router.go(-1);
            }
          })
          .catch((error) => {
            console.error('Error during article approval/rejection:', error);
            this.handleError(error);
          });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
/* Dialog styling for approval/rejection remarks */
.approval-remark-popup {
  border-radius: 5px;
  .el-dialog__header {
    border-bottom: 1px solid #c7c7c77d;
  }
  .el-dialog__footer {
    border-top: 1px solid #c7c7c77d;
  }
  .el-dialog__body {
    padding: 1rem 1rem 0;
    word-break: break-word;
    color: #767396;

    p{
      margin-bottom: 1rem;
    }
  }
  .btn-cancel {
    color: #a8a8a8;
  }
}
</style>
